import type { MiniAppAccountChoiceResponse } from '@repo/api/web/models'
import { computed, ref } from 'vue'

import { bindPlatformPayload } from '../bindPlatformPayload'
import {
  attemptPlatformAuth,
  completePlatformAccountSelect,
} from '../platformAuth'
import type { PlatformLaunchPayload } from '../ports/types'

export type PlatformGateReason = 'no-payload' | 'missing-host' | 'auth-failed'
export type PlatformGateStatus
  = | 'pending'
    | 'ready'
    | 'blocked'
    | 'account-choice'

const status = ref<PlatformGateStatus>('pending')
const reason = ref<PlatformGateReason | null>(null)
const accountChoice = ref<MiniAppAccountChoiceResponse | null>(null)
const isRetrying = ref(false)
const isSelectingAccount = ref(false)

let bootstrapped = false
let bootstrapPromise: Promise<void> | null = null

/**
 * Clears leftover CustomerSession material so a stored Bearer cannot skip
 * platform auth or unlock the catalog after a failed POST (ADR / FR).
 * Local reset only — no anonymous sign-in, no API logout.
 * @returns false when reset could not run; caller must fail closed.
 */
function discardPreviousSessionKeys(): boolean {
  try {
    const { service } = useAuth()
    service.resetLocalClientState()
    return true
  }
  catch (error) {
    console.error(
      '[platform-gate] failed to discard previous session keys',
      error,
    )
    return false
  }
}

function blockAuthFailed() {
  status.value = 'blocked'
  reason.value = 'auth-failed'
  accountChoice.value = null
}

function blockMissingHost() {
  status.value = 'blocked'
  reason.value = 'missing-host'
  accountChoice.value = null
}

async function evaluatePayload(payload: PlatformLaunchPayload) {
  accountChoice.value = null

  if (!discardPreviousSessionKeys()) {
    blockAuthFailed()
    return
  }

  if (payload.kind !== 'none' && !useAppConfig().host.trim()) {
    blockMissingHost()
    return
  }

  if (!payload.raw) {
    status.value = 'blocked'
    reason.value = 'no-payload'
    return
  }

  const authResult = await attemptPlatformAuth(payload)
  switch (authResult.status) {
    case 'ok':
      status.value = 'ready'
      reason.value = null
      return
    case 'accountChoice': {
      const accounts = authResult.choice.accounts ?? []
      if (accounts.length === 0) {
        // Fail closed: empty picker is not a valid entry path.
        blockAuthFailed()
        return
      }
      accountChoice.value = authResult.choice
      status.value = 'account-choice'
      reason.value = null
      return
    }
    case 'fail':
      blockAuthFailed()
      return
    default: {
      const _exhaustive: never = authResult
      void _exhaustive
      blockAuthFailed()
    }
  }
}

async function runBootstrap() {
  const { payload } = usePlatform()
  await evaluatePayload(payload)
}

/**
 * Gate host: whether the Mini App shell (catalog / tabs) may mount.
 * `canEnterApp` is true only after successful mini-app auth
 * (`completed` or select → completed).
 */
export function usePlatformSessionGate() {
  const canEnterApp = computed(() => status.value === 'ready')

  async function ensureStarted() {
    if (bootstrapped) {
      return bootstrapPromise ?? Promise.resolve()
    }
    bootstrapped = true
    bootstrapPromise = (async () => {
      try {
        await runBootstrap()
      }
      catch (error) {
        console.error('[platform-gate] bootstrap failed', error)
        blockAuthFailed()
      }
    })()
    await bootstrapPromise
  }

  async function retry() {
    if (isRetrying.value) {
      return
    }
    isRetrying.value = true
    try {
      const nuxtApp = useNuxtApp()
      const { adapter } = usePlatform()
      const payload = await adapter.init()
      bindPlatformPayload(nuxtApp, payload)
      await evaluatePayload(payload)
    }
    catch (error) {
      console.error('[platform-gate] retry failed', error)
      blockAuthFailed()
    }
    finally {
      isRetrying.value = false
    }
  }

  async function selectAccount(userId: number) {
    const choice = accountChoice.value
    if (!choice?.token || isSelectingAccount.value) {
      return
    }

    isSelectingAccount.value = true
    try {
      const result = await completePlatformAccountSelect(choice.token, userId)
      if (result === 'ok') {
        status.value = 'ready'
        reason.value = null
        accountChoice.value = null
        return
      }
      blockAuthFailed()
    }
    catch (error) {
      console.error('[platform-gate] select account failed', error)
      blockAuthFailed()
    }
    finally {
      isSelectingAccount.value = false
    }
  }

  return {
    status,
    reason,
    accountChoice,
    canEnterApp,
    isRetrying,
    isSelectingAccount,
    ensureStarted,
    retry,
    selectAccount,
  }
}
